/**
 * Gateway data the widget reads (history, a conversation's messages, MAIA
 * Chats' patient list) goes through react-query, cached in this client.
 *
 * Module level on purpose, like the chat store (hooks/useMaiaChat.ts): the
 * dashboard keeps the tile mounted while its expanded copy is open, and both
 * copies render `Providers` with this same client, so they share one cache.
 * Two copies asking for the same data at once send one request.
 *
 * react-query 3 because the monorepo is on TypeScript 3.9; @tanstack/react-query
 * v4 needs TypeScript 4.1+.
 */
import { QueryClient } from 'react-query';

import type { PatientChatQuestionRange } from '@mummssoftware/utils';

/** Query keys, in one place so a refresh can target them. */
export const maiaQueryKeys = {
  all: ['maia'] as const,
  history: (
    agency: string,
    patientNumber: string,
    range?: PatientChatQuestionRange | null,
  ) =>
    [
      'maia',
      'history',
      agency,
      patientNumber,
      range?.from ?? null,
      range?.to ?? null,
    ] as const,
  /** Every MAIA Chats list, whatever the agency or dates. */
  allPatients: ['maia', 'patients'] as const,
  messages: (conversationId: number) =>
    ['maia', 'messages', conversationId] as const,
  unanswered: (conversationId: number) =>
    ['maia', 'unanswered', conversationId] as const,
  patients: (
    agency: string,
    siteId: string | undefined,
    range?: PatientChatQuestionRange | null,
  ) =>
    [
      'maia',
      'patients',
      agency,
      siteId ?? null,
      range?.from ?? null,
      range?.to ?? null,
    ] as const,
};

export const createMaiaQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // the gateway's errors are shown as they are; a retry would only delay them
        retry: false,
        // reload when a view is shown (see each hook), not on tab focus or reconnect
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    },
  });

export const maiaQueryClient = createMaiaQueryClient();
